import React, { useState, useEffect } from 'react';
import { PlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import SetListsTable from '../components/SetListsTable';
import NewSetListModal from '../components/NewSetListModal';
import EditSetListModal from '../components/EditSetListModal';
import { getSetLists, createSetList, updateSetList, deleteSetList } from '../services/setlistApi';
import { getSongs } from '../services/api';

const SetListsPage = () => {
  const [setlists, setSetlists] = useState([]);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewSetListModal, setShowNewSetListModal] = useState(false);
  const [editingSetList, setEditingSetList] = useState(null);
  const [createError, setCreateError] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadSetLists();
  }, []);

  const loadSetLists = async () => {
    try {
      setLoading(true);
      const [setlistsData, songsData] = await Promise.all([
        getSetLists(),
        getSongs()
      ]);
      setSetlists(setlistsData);
      setSongs(songsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSetList = async (setlistData) => {
    setIsCreating(true);
    setCreateError(null);
    try {
      const newSetList = await createSetList(setlistData);
      setSetlists([...setlists, newSetList]);
      setShowNewSetListModal(false);
    } catch (error) {
      console.error('Error creating setlist:', error);
      setCreateError(error.message || 'Failed to create setlist. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteSetList = async (setlistId) => {
    if (window.confirm('Are you sure you want to delete this set list?')) {
      try {
        await deleteSetList(setlistId);
        setSetlists(setlists.filter(setlist => setlist.id !== setlistId));
      } catch (error) {
        console.error('Error deleting setlist:', error);
      }
    }
  };

  const handleEditSetList = (setlist) => {
    setEditingSetList(setlist);
  };

  const handleUpdateSetList = async (setlistData) => {
    try {
      const updatedSetList = await updateSetList(editingSetList.id, setlistData);
      setSetlists(setlists.map(setlist => 
        setlist.id === editingSetList.id ? updatedSetList : setlist
      ));
      setEditingSetList(null);
    } catch (error) {
      console.error('Error updating setlist:', error);
    }
  };

  const filteredSetLists = setlists.filter(setlist => {
    if (!searchTerm) return true;
    
    return setlist.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           new Date(setlist.date).toLocaleDateString().includes(searchTerm);
  });

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Set Lists</h1>
        
        {/* Action Bar */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-4">
            {/* Search Bar */}
            <div className="relative">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search set lists..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* New Set List Button */}
            <button
              onClick={() => setShowNewSetListModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center space-x-2"
            >
              <PlusIcon className="w-5 h-5" />
              <span>New Set List</span>
            </button>
          </div>
        </div>

        {/* Set Lists Table */}
        {loading ? (
          <div className="text-center py-8">Loading set lists...</div>
        ) : (
          <SetListsTable
            setlists={filteredSetLists}
            songs={songs}
            onEdit={handleEditSetList}
            onDelete={handleDeleteSetList}
          />
        )}
      </div>

      {/* Modals */}
      {showNewSetListModal && (
        <NewSetListModal
          onClose={() => { setShowNewSetListModal(false); setCreateError(null); }}
          onSave={handleCreateSetList}
          error={createError}
          isLoading={isCreating}
        />
      )}

      {editingSetList && (
        <EditSetListModal
          setlist={editingSetList}
          onClose={() => setEditingSetList(null)}
          onSave={handleUpdateSetList}
        />
      )}
    </div>
  );
};

export default SetListsPage;